import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Activity, Company, Lead, Opportunity } from '../types'; // Added Opportunity and Company
import { EmployeeProvider } from './EmployeeContext';

interface CRMContextType {
  leads: Lead[];
  activities: Activity[];
  opportunities: Opportunity[]; // Added opportunities
  companies: Company[]; // Added companies
  addLead: (lead: Omit<Lead, 'id' | 'createdDate' | 'lastActivity'>) => void;
  updateLead: (id: string, lead: Partial<Lead>) => void;
  deleteLead: (id: string) => void;
  addActivity: (activity: Omit<Activity, 'id'>) => void;
  getLeadActivities: (leadId: string) => Activity[];
  addOpportunity: (opportunity: Omit<Opportunity, 'id' | 'createdDate' | 'lastModifiedDate'>) => void;
  updateOpportunity: (id: string, opportunity: Partial<Omit<Opportunity, 'id' | 'createdDate' | 'lastModifiedDate'>>) => void;
  deleteOpportunity: (id: string) => void;
  addCompany: (company: Omit<Company, 'id'>) => void;
  updateCompany: (id: string, company: Partial<Company>) => void;
  deleteCompany: (id: string) => void;
}

const CRMContext = createContext<CRMContextType | undefined>(undefined);

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
};

interface CRMProviderProps {
  children: ReactNode;
}

export const CRMProvider: React.FC<CRMProviderProps> = ({ children }) => {
  const [leads, setLeads] = useState<Lead[]>(() => {
    const savedLeads = localStorage.getItem('crm_leads');
    return savedLeads ? JSON.parse(savedLeads) : [];
  });

  const [activities, setActivities] = useState<Activity[]>(() => {
    const savedActivities = localStorage.getItem('crm_activities');
    return savedActivities ? JSON.parse(savedActivities) : [];
  });

  const [companies, setCompanies] = useState<Company[]>(() => {
    const mockCompanies: Company[] = [
      {
        id: '1',
        name: 'TechCorp Solutions',
        website: 'www.techcorp.com',
        industry: 'Technology',
        size: '100-500',
        phone: '+1 (555) 123-4567',
        email: 'contact@techcorp.com',
        address: '123 Tech Street, San Francisco, CA 94105',
        status: 'Active',
        accountOwner: 'Sarah Chen',
        lastActivity: '2 hours ago',
        dealValue: '$125,000',
        employees: 250
      },
      {
        id: '2',
        name: 'Global Manufacturing Inc',
        website: 'www.globalmfg.com',
        industry: 'Manufacturing',
        size: '500+',
        phone: '+1 (555) 234-5678',
        email: 'info@globalmfg.com',
        address: '456 Industrial Blvd, Detroit, MI 48201',
        status: 'Active',
        accountOwner: 'Carol Carter',
        lastActivity: '1 day ago',
        dealValue: '$350,000',
        employees: 1200
      },
      {
        id: '3',
        name: 'Healthcare Partners',
        website: 'www.healthpartners.com',
        industry: 'Healthcare',
        size: '50-100',
        phone: '+1 (555) 345-6789',
        email: 'contact@healthpartners.com',
        address: '789 Medical Center Dr, Boston, MA 02115',
        status: 'Prospect',
        accountOwner: 'Alex Drago',
        lastActivity: '3 hours ago',
        dealValue: '$75,000',
        employees: 85
      },
      {
        id: '4',
        name: 'Financial Services Group',
        website: 'www.fsggroup.com',
        industry: 'Financial Services',
        size: '200-500',
        phone: '+1 (555) 456-7890',
        email: 'hello@fsggroup.com',
        address: '321 Wall Street, New York, NY 10005',
        status: 'Active',
        accountOwner: 'Frank Wright',
        lastActivity: '5 minutes ago',
        dealValue: '$200,000',
        employees: 350
      },
      {
        id: '5',
        name: 'Retail Innovations',
        website: 'www.retailinnovations.com',
        industry: 'Retail',
        size: '20-50',
        phone: '+1 (555) 567-8901',
        email: 'support@retailinnovations.com',
        address: '654 Commerce Ave, Chicago, IL 60601',
        status: 'Inactive',
        accountOwner: 'Sarah Mitchell',
        lastActivity: '1 week ago',
        dealValue: '$45,000',
        employees: 35
      }
    ];
    const saved = localStorage.getItem('crm_companies');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Merge user-saved companies with mock ones, without duplicates
          const merged = [
            ...parsed,
            ...mockCompanies.filter(mock => !parsed.some((company: Company) => company.id === mock.id)),
          ];
          return merged;
        }
      } catch {
        console.error('Failed to parse companies from localStorage');
      }
    }
    // No saved data or invalid data: return mock companies
    return mockCompanies;
  });

  const [opportunities, setOpportunities] = useState<Opportunity[]>(() => {
    const mockOpportunities: Opportunity[] = [
      // Leads for Innovatech Solutions
      {
        id: "mock-opp-1",
        companyName: "Innovatech Solutions",
        contactName: "Alice Wonderland",
        contactEmail: "alice.wonderland@innovatech.com",
        dealStage: "Qualification",
        companySize: "21-100",
        expectedDealSize: "30,000",
        currency: "USD",
        targetGoLiveDate: "2025-08-15",
        dealType: "New Business",
        salesRep: "David Rodriguez",
        soc2: true,
        salesRegionTerritory: "North America - West",
        notes: "Initial contact made, strong interest in AI-driven analytics.",
        createdDate: "2025-05-01T10:00:00.000Z",
        lastModifiedDate: "2025-05-20T14:30:00.000Z",
      },
      {
        id: "mock-opp-8",
        companyName: "Innovatech Solutions",
        contactName: "Charlie Bucket",
        contactEmail: "charlie.bucket@innovatech.com",
        dealStage: "Negotiation",
        companySize: "100-500",
        expectedDealSize: "80,000",
        currency: "USD",
        targetGoLiveDate: "2025-08-01",
        dealType: "Existing Business",
        salesRep: "Sarah Mitchell",
        soc2: true,
        salesRegionTerritory: "APAC",
        notes: "Finalizing the contract. Looks promising.",
        createdDate: "2025-06-01T10:00:00.000Z",
        lastModifiedDate: "2025-06-02T09:00:00.000Z",
      },

      // Leads for Synergy Corp
      {
        id: "mock-opp-2",
        companyName: "Synergy Corp",
        contactName: "Charlie Brown",
        contactEmail: "charlie.brown@synergycorp.com",
        dealStage: "Proposal Sent",
        companySize: "500+",
        expectedDealSize: "55,000",
        currency: "EUR",
        targetGoLiveDate: "2025-09-01",
        dealType: "Existing Business",
        salesRep: "Marcus Johnson",
        soc2: false,
        salesRegionTerritory: "EMEA - Central",
        notes: "Proposal for expansion module delivered. Awaiting feedback.",
        createdDate: "2025-04-10T09:00:00.000Z",
        lastModifiedDate: "2025-05-28T11:00:00.000Z",
      },

      // Leads for Future Gadgets Ltd.
      {
        id: "mock-opp-3",
        companyName: "Future Gadgets Ltd.",
        contactName: "Diana Prince",
        contactEmail: "diana.prince@futuregadgets.com",
        dealStage: "Discovery / Demo Scheduled",
        companySize: "6-20",
        expectedDealSize: "12,000",
        currency: "USD",
        targetGoLiveDate: "2025-07-20",
        dealType: "New Business",
        salesRep: "Priya Patel",
        soc2: true,
        salesRegionTerritory: "North America - East",
        notes: "Demo scheduled for next week. Key focus on integration capabilities.",
        createdDate: "2025-05-15T16:00:00.000Z",
        lastModifiedDate: "2025-05-25T09:45:00.000Z",
      },

      // Leads for QuantumLeap AI
      {
        id: "mock-opp-4",
        companyName: "QuantumLeap AI",
        contactName: "Eve Adams",
        contactEmail: "eve.adams@quantumleap.ai",
        dealStage: "Negotiation",
        companySize: "100-500",
        expectedDealSize: "85,000",
        currency: "USD",
        targetGoLiveDate: "2025-10-01",
        dealType: "New Business",
        salesRep: "Frank Wright",
        soc2: true,
        salesRegionTerritory: "APAC",
        notes: "Negotiating terms for enterprise license.",
        createdDate: "2025-03-20T11:00:00.000Z",
        lastModifiedDate: "2025-06-01T10:00:00.000Z",
      },

      // Leads for DataWeavers Inc.
      {
        id: "mock-opp-5",
        companyName: "DataWeavers Inc.",
        contactName: "Grace Hopper",
        contactEmail: "grace.hopper@dataweavers.com",
        dealStage: "Closed Won",
        companySize: "500+",
        expectedDealSize: "120,000",
        currency: "USD",
        targetGoLiveDate: "2025-06-30",
        dealType: "New Business",
        salesRep: "Carol Carter",
        soc2: true,
        salesRegionTerritory: "North America - Central",
        notes: "Contract signed. Implementation phase starting.",
        createdDate: "2025-02-15T09:30:00.000Z",
        lastModifiedDate: "2025-05-30T17:00:00.000Z",
      },

      // Leads for NextGen Pharma
      {
        id: "mock-opp-6",
        companyName: "NextGen Pharma",
        contactName: "Heidi Klum",
        contactEmail: "heidi.klum@nextgenpharma.com",
        dealStage: "Closed Lost",
        companySize: "100-500",
        expectedDealSize: "40,000",
        currency: "GBP",
        targetGoLiveDate: "2025-08-01",
        dealType: "New Business",
        salesRep: "James Wilson",
        soc2: false,
        salesRegionTerritory: "EMEA - UK",
        notes: "Lost to competitor due to pricing.",
        createdDate: "2025-04-01T14:00:00.000Z",
        lastModifiedDate: "2025-05-18T12:00:00.000Z",
      },

      // Leads for DeltaCore
      {
        id: "mock-opp-7",
        companyName: "DeltaCore",
        contactName: "Jordan Drago",
        contactEmail: "jordan.drago@deltacore.com",
        dealStage: "Negotiation",
        companySize: "21-100",
        expectedDealSize: "35,000",
        currency: "CAD",
        targetGoLiveDate: "2025-10-10",
        dealType: "New Business",
        salesRep: "Alex Drago",
        soc2: false,
        salesRegionTerritory: "San Francisco, California",
        notes: "",
        createdDate: "2025-05-22T08:00:00.000Z",
        lastModifiedDate: "2025-06-02T11:30:00.000Z",
      },

      // Leads for GreenEnergy Solutions
      {
        id: "mock-opp-14",
        companyName: "GreenEnergy Solutions",
        contactName: "Judy Smith",
        contactEmail: "judy.smith@greenenergy.com",
        dealStage: "Proposal Sent",
        companySize: "1-5",
        expectedDealSize: "60,000",
        currency: "EUR",
        targetGoLiveDate: "2025-11-15",
        dealType: "Existing Business",
        salesRep: "Carol Carter",
        soc2: false,
        salesRegionTerritory: "EMEA - Nordics",
        notes: "Upsell opportunity for new solar panel management software.",
        createdDate: "2025-05-10T10:30:00.000Z",
        lastModifiedDate: "2025-05-29T16:45:00.000Z",
      },

      // Leads for HealthPlus Diagnostics
      {
        id: "mock-opp-9",
        companyName: "HealthPlus Diagnostics",
        contactName: "Ken Jeong",
        contactEmail: "ken.jeong@healthplus.com",
        dealStage: "Discovery / Demo Scheduled",
        companySize: "21-100",
        expectedDealSize: "40,000",
        currency: "USD",
        targetGoLiveDate: "2025-09-20",
        dealType: "New Business",
        salesRep: "Maria Garcia",
        soc2: true,
        salesRegionTerritory: "North America - South",
        notes: "Referred by Innovatech Solutions. Demo is critical.",
        createdDate: "2025-05-15T16:00:00.000Z",
        lastModifiedDate: "2025-05-25T09:45:00.000Z",
      },

      // Leads for Apex Innovations
      {
        id: "mock-opp-10",
        companyName: "Apex Innovations",
        contactName: "Larry David",
        contactEmail: "larry.david@apexinnovations.com",
        dealStage: "Qualification",
        companySize: "1-5",
        expectedDealSize: "7,500",
        currency: "USD",
        targetGoLiveDate: "2025-07-30",
        dealType: "New Business",
        salesRep: "Alex Drago",
        soc2: true,
        salesRegionTerritory: "San Francisco, California",
        notes: "Early stage startup, budget constrained but high potential.",
        createdDate: "2025-06-01T11:20:00.000Z",
        lastModifiedDate: "2025-06-02T15:00:00.000Z",
      },

      // Leads for TechTory
      {
        id: "mock-opp-11",
        companyName: "TechTory",
        contactName: "Leo Vance",
        contactEmail: "leo.vance@techtory.com",
        dealStage: "Negotiation",
        companySize: "21-100",
        expectedDealSize: "25,000",
        salesRep: "Kevin Lee",
        createdDate: "2025-07-01T10:00:00.000Z",
        lastModifiedDate: "2025-07-03T11:00:00.000Z",
        dealType: "New Business",
      },
      {
        id: "mock-opp-12",
        companyName: "TechTory",
        contactName: "Mia Wallace",
        contactEmail: "mia.wallace@techtory.com",
        dealStage: "Closed Won",
        companySize: "21-100",
        expectedDealSize: "35,000",
        salesRep: "Frank Wright",
        createdDate: "2025-06-12T09:00:00.000Z",
        lastModifiedDate: "2025-07-01T11:00:00.000Z",
        dealType: "New Business",
      },

      // Leads for Smarket
      {
        id: "mock-opp-13",
        companyName: "Smarket",
        contactName: "Jules Winnfield",
        contactEmail: "jules.winnfield@smarket.com",
        dealStage: "Qualification",
        companySize: "500+",
        expectedDealSize: "65,000",
        salesRep: "Carol Carter",
        createdDate: "2025-07-02T09:00:00.000Z",
        lastModifiedDate: "2025-07-03T11:00:00.000Z",
        dealType: "Existing Business",
      },

      // Leads for Shopment
      {
        id: "mock-opp-15",
        companyName: "Shopment",
        contactName: "Beatrix Kiddo",
        contactEmail: "beatrix.kiddo@shopment.com",
        dealStage: "Proposal Sent",
        companySize: "1-20",
        expectedDealSize: "30,000",
        salesRep: "Aisha Johnson",
        createdDate: "2025-06-28T16:00:00.000Z",
        lastModifiedDate: "2025-07-02T09:45:00.000Z",
        dealType: "New Business",
      },

      // Leads for Grocer Jar
      {
        id: "mock-opp-16",
        companyName: "Grocer Jar",
        contactName: "Aldo Raine",
        contactEmail: "aldo.raine@grocerjar.com",
        dealStage: "Closed Lost",
        companySize: "21-100",
        expectedDealSize: "12,000",
        salesRep: "Robert Kim",
        createdDate: "2025-06-15T11:00:00.000Z",
        lastModifiedDate: "2025-07-01T17:00:00.000Z",
        dealType: "New Business",
        notes: "Went with a competitor."
      },

      // Leads for Wholesale Centers
      {
        id: "mock-opp-17",
        companyName: "Wholesale Centers",
        contactName: "Django Freeman",
        contactEmail: "django.freeman@wholesalecenters.com",
        dealStage: "Proposal Sent",
        companySize: "500+",
        expectedDealSize: "180,000",
        salesRep: "Frank Wright",
        createdDate: "2025-06-20T11:00:00.000Z",
        lastModifiedDate: "2025-07-02T17:00:00.000Z",
        dealType: "Existing Business",
      },

      // Leads for Crafery
      {
        id: "mock-opp-18",
        companyName: "Crafery",
        contactName: "Rick Dalton",
        contactEmail: "rick.dalton@crafery.com",
        dealStage: "Discovery / Demo Scheduled",
        companySize: "1-20",
        expectedDealSize: "15,000",
        salesRep: "Carol Carter",
        createdDate: "2025-07-01T11:00:00.000Z",
        lastModifiedDate: "2025-07-02T17:00:00.000Z",
        dealType: "New Business",
      },

      // Leads for QuantumLeap Corp
      {
        id: "mock-opp-19",
        companyName: "QuantumLeap Corp",
        contactName: "Mike Teavee",
        contactEmail: "mike.teavee@quantumleap.com",
        dealStage: "Closed Won",
        companySize: "21-100",
        expectedDealSize: "50,000",
        salesRep: "Alex Drago",
        createdDate: "2025-04-28T11:30:00.000Z",
        lastModifiedDate: "2025-06-25T15:00:00.000Z",
        dealType: "New Business",
      },

      // Leads for Stellar Solutions
      {
        id: "mock-opp-20",
        companyName: "Stellar Solutions",
        contactName: "Violet Beauregarde",
        contactEmail: "violet.beauregarde@stellarsolutions.com",
        dealStage: "Discovery / Demo Scheduled",
        companySize: "1-20",
        expectedDealSize: "65,000",
        salesRep: "Frank Wright",
        createdDate: "2025-06-18T09:00:00.000Z",
        lastModifiedDate: "2025-07-01T10:20:00.000Z",
        dealType: "Existing Business",
      },

      // Leads for Nexus Enterprises
      {
        id: "mock-opp-21",
        companyName: "Nexus Enterprises",
        contactName: "Augustus Gloop",
        contactEmail: "augustus.gloop@nexusenterprises.com",
        dealStage: "Negotiation",
        companySize: "500+",
        expectedDealSize: "95,000",
        salesRep: "Jennifer Martinez",
        createdDate: "2025-06-10T12:00:00.000Z",
        lastModifiedDate: "2025-07-03T18:00:00.000Z",
        dealType: "New Business",
      }
    ];
    const saved = localStorage.getItem('crm_opportunities');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Merge user-saved opportunities with mock ones, without duplicates
          const merged = [
            ...parsed,
            ...mockOpportunities.filter(mock => !parsed.some((op: Opportunity) => op.id === mock.id)),
          ];
          return merged;
        }
      } catch {
        console.error('Failed to parse opportunities from localStorage');
      }
    }
    // No saved data or invalid data: return mock opportunities
    return mockOpportunities;
  });

  useEffect(() => {
    localStorage.setItem('crm_leads', JSON.stringify(leads));
  }, [leads]);

  useEffect(() => {
    localStorage.setItem('crm_activities', JSON.stringify(activities));
  }, [activities]);

  useEffect(() => {
    localStorage.setItem('crm_opportunities', JSON.stringify(opportunities));
  }, [opportunities]);

  useEffect(() => {
    localStorage.setItem('crm_companies', JSON.stringify(companies));
  }, [companies]);

  const addLead = (leadData: Omit<Lead, 'id' | 'createdDate' | 'lastActivity'>) => {
    const newLead: Lead = {
      ...leadData,
      id: Date.now().toString(),
      createdDate: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
    };
    setLeads([...leads, newLead]);
  };

  const updateLead = (id: string, leadData: Partial<Lead>) => {
    setLeads(leads.map(lead => 
      lead.id === id 
        ? { ...lead, ...leadData, lastActivity: new Date().toISOString() }
        : lead
    ));
  };

  const deleteLead = (id: string) => {
    setLeads(leads.filter(lead => lead.id !== id));
    setActivities(activities.filter(activity => activity.leadId !== id));
  };

  const addActivity = (activityData: Omit<Activity, 'id'>) => {
    const newActivity: Activity = {
      ...activityData,
      id: Date.now().toString(),
    };
    setActivities([...activities, newActivity]);
    
    // Update lead's last activity
    const lead = leads.find(l => l.id === activityData.leadId);
    if (lead) {
      updateLead(lead.id, { lastActivity: activityData.date });
    }
  };

  const getLeadActivities = (leadId: string) => {
    return activities.filter(activity => activity.leadId === leadId);
  };

  const addOpportunity = (opportunityData: Omit<Opportunity, 'id' | 'createdDate' | 'lastModifiedDate'>) => {
    const newOpportunity: Opportunity = {
      ...opportunityData,
      id: Date.now().toString(),
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString(),
    };
    setOpportunities(prev => [...prev, newOpportunity]);
  };

  const updateOpportunity = (id: string, opportunityData: Partial<Omit<Opportunity, 'id' | 'createdDate' | 'lastModifiedDate'>>) => {
    setOpportunities(prev => 
      prev.map(op => 
        op.id === id 
          ? { ...op, ...opportunityData, lastModifiedDate: new Date().toISOString() } 
          : op
      )
    );
  };

  const deleteOpportunity = (id: string) => {
    setOpportunities(prev => prev.filter(op => op.id !== id));
  };

  const addCompany = (companyData: Omit<Company, 'id'>) => {
    const newCompany: Company = {
      ...companyData,
      id: Date.now().toString(),
    };
    setCompanies(prev => [...prev, newCompany]);
  };

  const updateCompany = (id: string, companyData: Partial<Company>) => {
    setCompanies(prev => 
      prev.map(company => 
        company.id === id 
          ? { ...company, ...companyData, lastActivity: new Date().toLocaleString() } 
          : company
      )
    );
  };

  const deleteCompany = (id: string) => {
    setCompanies(prev => prev.filter(company => company.id !== id));
  };

  return (
    <EmployeeProvider>
      <CRMContext.Provider value={{
        leads,
        activities,
        addLead,
        updateLead,
        deleteLead,
        addActivity,
        getLeadActivities,
        opportunities, // Added opportunities
        addOpportunity,
        updateOpportunity,
        deleteOpportunity,
        companies, // Added companies
        addCompany,
        updateCompany,
        deleteCompany,
      }}>
        {children}
      </CRMContext.Provider>
    </EmployeeProvider>
  );
}; 